import type { Dwrtcl1, Dwrtpc, Dwrtpt1 } from '@/apiFzu/ApiFzu'
import * as Cesium from 'cesium'
import type { LineString, Polygon } from 'geojson'
import centerlineFragment from './centerLine.frag.glsl?raw'
import centerLinePng from '@/assets/centerline.png?url'
import centerLineRightPng from '@/assets/centerLineRight.png?url'
import centerLineLeftPng from '@/assets/centerLineLeft.png?url'
/** 高性能绘制航道边缘线 */
export function drawPurpleLine(data: Dwrtpc[]) {
  // 合并所有几何实例
  const instances = data.map((el) => {
    const coordinates = (el.geom as unknown as LineString).coordinates
    // 预转换坐标避免重复计算
    const positions = Cesium.Cartesian3.fromDegreesArray(
      coordinates.flat(1) // 仅展开一层提高性能
    )

    return new Cesium.GeometryInstance({
      geometry: new Cesium.GroundPolylineGeometry({
        positions,
        width: 1.0,
      }),
      attributes: {
        // 添加实例级颜色属性（可选）
        color: new Cesium.ColorGeometryInstanceAttribute(0.5, 0.0, 0.5, 1.0),
      },
    })
  })

  // 共享材质实例
  const dashMaterial = Cesium.Material.fromType('PolylineDash', {
    color: Cesium.Color.PURPLE,
    dashLength: 32.0,
    dashPattern: 255, // 优化为二进制模式 11111111
  })

  // 创建单一 Primitive
  const primitive = new Cesium.GroundPolylinePrimitive({
    geometryInstances: instances,
    appearance: new Cesium.PolylineMaterialAppearance({
      material: dashMaterial,
      translucent: false, // 禁用透明度提升渲染性能
    }),
    // 优化渲染批次
    interleave: true,
  })

  // 添加内存优化逻辑
  const cleanup = () => {
    dashMaterial.destroy()
  }
  primitive.destroy = () => {
    cleanup()
    Cesium.GroundPolylinePrimitive.prototype.destroy.call(primitive)
  }

  return [primitive] // 返回单个 Primitive 数组
}

/**绘制航道中心线 */
export function drawDwrtCenterline(data: Dwrtcl1[]) {
  const material = new Cesium.Material({
    fabric: {
      type: 'DoubleArrow',
      uniforms: {
        image: centerLinePng, // 箭头图片
        dashLength: 200,
      },
      source: centerlineFragment,
    },
    translucent: true,
  })
  const rightMaterial = new Cesium.Material({
    fabric: {
      type: 'SingleArrow',
      uniforms: {
        image: centerLineRightPng, // 箭头图片
        dashLength: 200,
      },
      source: centerlineFragment,
    },
    translucent: true,
  })
  const leftMaterial = new Cesium.Material({
    fabric: {
      type: 'SingleArrow',
      uniforms: {
        image: centerLineLeftPng, // 箭头图片
        dashLength: 200,
      },
      source: centerlineFragment,
    },
    translucent: true,
  })
  return data.map((el) => {
    const positions = Cesium.Cartesian3.fromDegreesArray(
      (el.geom as unknown as LineString).coordinates.flat()
    )
    const groundPolylineGeometry = new Cesium.GroundPolylineGeometry({
      positions: positions,
      width: 15.0,
    })
    let m
    if (el.trafic === 1) {
      m = rightMaterial
    } else if (el.trafic === 4) {
      m = material
    } else if (el.trafic === 2) {
      m = leftMaterial
    }
    return new Cesium.GroundPolylinePrimitive({
      geometryInstances: new Cesium.GeometryInstance({
        geometry: groundPolylineGeometry,
      }),
      appearance: new Cesium.PolylineMaterialAppearance({
        material: m,
      }),
    })
  })
}

/**绘制航道面 */
export function drawDwrtPolygon(data: Dwrtpt1[]) {
  return data.map((el) => {
    const geom = el.geom as unknown as Polygon
    const coordinates = geom.coordinates[0]
    const flatCoordinates = coordinates.flat()
    // 使用 Cesium.Cartesian3.fromDegreesArray 转换为 Cartesian3 坐标
    const positions = Cesium.Cartesian3.fromDegreesArray(flatCoordinates)

    const geometry = new Cesium.PolygonGeometry({
      polygonHierarchy: new Cesium.PolygonHierarchy(positions),
      height: 0, // 高度为 0，贴地面
    })
    // 创建 GeometryInstance
    const polygonInstance = new Cesium.GeometryInstance({
      geometry,
      attributes: {
        color: Cesium.ColorGeometryInstanceAttribute.fromColor(Cesium.Color.GREEN.withAlpha(0.5)), // 设置绿色，带透明度
      },
    })
    return new Cesium.GroundPrimitive({
      geometryInstances: polygonInstance,
    })
  })
}
